import { template } from "@ember/template-compiler";
import { hash } from '@ember/helper';
import { Compiled } from '../services/kolay/compiler/reactive.ts';
import { Load } from './utils.gts';
import type { TOC } from '@ember/component/template-only';
import type { ArrayType, DeclarationReference, DeclarationReflection, LiteralType, NamedTupleMember, ReferenceType, SignatureReflection, SomeType, TupleType, UnionType, UnknownType } from 'typedoc';
/**
 * Assumptions:
 * - we are documenting public API
 *   - component properties and methods are not public API
 *     - including the constructor, inherited methods, etc
 *   - only the signature describes what the public API is.
 */ export const APIDocs: TOC<{
    Args: {
        /**
     * Which module to import the type from
     */ module: string;
        /**
     * The name of the export to render the type / JSDoc of
     */ name: string;
        /**
     * The name of the package to lookup the module and export name.
     */ package: string;
    };
}> = template(`
  <Load @module={{@module}} @name='{{@name}}' @package={{@package}} as |info|>
    <Declaration @info={{info}} />
  </Load>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
/**
 * Used for referencing the comment on a const or class.
 *
 * For example:
 * ```
 * /*
 *  * Comment block here is what is targeted
 *  *\/
 * export const CommentQuery ...
 * ```
 *
 * Usage:
 * ```hbs
 * <CommentQuery @name="CommentQuery" ... />
 * ```
 */ export const CommentQuery: TOC<{
    Args: {
        /**
     * Which module to import the type from
     */ module: string;
        /**
     * The name of the export to render the type / JSDoc of
     */ name: string;
        /**
     * The name of the package to lookup the module and export name.
     */ package: string;
    };
}> = template(`
  <Load @package={{@package}} @module={{@module}} @name={{@name}} as |info|>
    <Comment @info={{info}} />
  </Load>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const join = (lines1: string[])=>lines1.join('\n');
const text = (lines1: {
    text: string;
}[])=>lines1.map((line1)=>line1.text);
export function isGlimmerComponent(info1: DeclarationReference) {
    let extended1 = (info1 as any)?.extendedTypes?.[0];
    if (!extended1) return false;
    return (extended1.name === 'default' && extended1.package === '@glimmer/component');
}
export const Comment: TOC<{
    Args: {
        info: {
            comment?: {
                summary?: {
                    text: string;
                }[];
            };
        };
    };
}> = template(`
  {{#if @info.comment.summary}}
    {{#let (Compiled (join (text @info.comment.summary))) as |compiled|}}
      {{#if compiled.isReady}}
        <div class='typedoc-rendered-comment'>
          <compiled.component />
        </div>
      {{/if}}
    {{/let}}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const isIgnored = (name1: string)=>[
        '__type',
        'TOC',
        'TemplateOnlyComponent'
    ].includes(name1);
const isConst = (x1: {
    flags: {
        isConst: boolean;
    };
})=>x1.flags.isConst;
const not = (x1: unknown)=>!x1;
const Declaration: TOC<{
    Args: {
        info: DeclarationReflection | undefined;
    };
}> = template(`
  {{#if @info}}
    <div class='typedoc__declaration'>
      {{#if (not (isIgnored @info.name))}}
        <span class='typedoc__declaration-name'>{{@info.name}}</span>
      {{/if}}

      {{#if (isConst @info)}}
        <Comment @info={{@info}} />
      {{/if}}

      {{#if @info.type}}
        <Type @info={{@info.type}} />
      {{/if}}

      {{#if @info.children}}
        <ul class='typedoc__declaration-children'>
          {{#each @info.children as |child|}}
            <li><Declaration @info={{child}} /></li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if @info.signatures}}
        <ul class='typedoc__declaration-signatures'>
          {{#each @info.signatures as |child|}}
            {{! @glint-expect-error }}
            <li><Type @info={{child}} /></li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if (not (isConst @info))}}
        {{#if @info.comment.summary}}
          <Comment @info={{@info}} />
        {{/if}}
      {{/if}}
    </div>
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Reflection: TOC<{
    info: {
        declaration: DeclarationReflection;
    };
}> = template(`<Declaration @info={{@info.declaration}} />`, {
    eval () {
        return eval(arguments[0]);
    }
});
const isReference = (x1: {
    type: string;
})=>x1?.type === 'reference';
const isReflection = (x1: {
    type: string;
})=>x1?.type === 'reflection';
export const isIntrinsic = (x1: {
    type: string;
})=>x1?.type === 'intrinsic';
const isTuple = (x1: {
    type: string;
})=>x1?.type === 'tuple';
export const isNamedTuple = (x1: SomeType | undefined): x is NamedTupleMember =>x1?.type === 'namedTupleMember';
const isVoidIntrinsic = (x1: unknown | undefined)=>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    if (typeof x1.type === 'object' && x1.type !== null) {
        if ('type' in x1.type && 'name' in x1.type) {
            return x1.type.type === 'intrinsic' && x1.type.name === 'void';
        }
    }
    return false;
};
const isArray = (x1: SomeType | undefined): x is ArrayType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'array';
};
const isFn = (x1: SomeType | undefined)=>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('name' in x1)) return false;
    if (!('variant' in x1)) return false;
    return x1.variant === 'signature';
};
const isUnknownType = (x1: SomeType | undefined): x is UnknownType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'unknown';
};
const isUnion = (x1: SomeType | undefined): x is UnionType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'union';
};
const isLiteral = (x1: SomeType | undefined): x is UnionType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'literal';
};
// function typeArg(info: DeclarationReference) {
//   let extended = info?.extendedTypes?.[0]
//   if (!extended) return false;
//   return extended.typeArguments[0]
// }
//
const isInvokable = (info1: ReferenceType)=>info1.name === 'Invokable';
const Reference: TOC<{
    info: ReferenceType;
}> = template(`
  {{#if (isInvokable @info)}}
    <div class='typedoc__unknown__yield'>
      <Intrinsic @info={{hash name='Component'}} />
    </div>
  {{else}}
    <div class='typedoc__reference'>
      {{#if (not (isIgnored @info.name))}}
        <div class='typedoc__reference__name'>{{@info.name}}</div>
      {{/if}}
      {{#if @info.typeArguments.length}}
        <div class='typedoc__reference__typeArguments'>
          &lt;
          {{#each @info.typeArguments as |typeArg|}}
            <div class='typedoc__reference__typeArgument'>
              <Type @info={{typeArg}} />
            </div>
          {{/each}}
          &gt;
        </div>
      {{/if}}
    </div>
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Intrinsic: TOC<{
    info: {
        name: string;
    };
}> = template(`
  <span class='typedoc__intrinsic'>{{@info.name}}</span>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const VoidIntrinsic: TOC<{
    info: {
        name: string;
    };
}> = template(`
  <div class='typedoc__void_intrinsic'>
    {{! @glint-expect-error }}
    <Function @info={{@info}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Tuple: TOC<{
    Args: {
        info: TupleType;
    };
}> = template(`
  {{#each @info.elements as |element|}}
    <Type @info={{element}} />
  {{/each}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export const NamedTuple: TOC<{
    Args: {
        info: NamedTupleMember;
    };
}> = template(`
  <div class='typedoc__named-tuple'>
    <div class='typedoc__name'>{{@info.name}}</div>
    <Type @info={{@info.element}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Array: TOC<{
    Args: {
        info: ArrayType;
    };
}> = template(`
  <div class='typedoc__array'>
    <div class='typedoc__array__indicator'>Array of</div>
    <Type @info={{@info.elementType}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Function: TOC<{
    Args: {
        info: SignatureReflection;
    };
}> = template(`
  <div class='typedoc__function'>
    <div class='typedoc__function_comment'>
      <Comment @info={{@info}} />
    </div>
    <div class='typedoc__function__type'>
      <div class='typedoc__function__open'>(</div>
      <div class='typedoc__function__parameters'>
        {{#each @info.parameters as |param|}}
          <div class='typedoc__function__parameter__container'>
            <div class='typedoc__function__parameter'>
              <div
                class='typedoc__function__parameter__name'
              >{{param.name}}</div>
              <div class='typedoc__function__parameter__type'>
                {{! @glint-expect-error }}
                <Type @info={{param.type}} />
              </div>
            </div>
            <div class='typedoc__function__parameter__comment'>
              <Comment @info={{param}} />
            </div>
          </div>
        {{/each}}
      </div>
      <div class='typedoc__function__close'>) =></div>
      <div class='typedoc__function__return_type'>
        {{! @glint-expect-error }}
        <Type @info={{@info.type}} />
      </div>
    </div>
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Unknown: TOC<{
    Args: {
        info: any;
    };
}> = template(`
  <div class='typedoc__unknown'>
    {{@info.name}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Union: TOC<{
    Args: {
        info: UnionType;
    };
}> = template(`
  <div class='typedoc__union'>
    {{#each @info.types as |type|}}
      <div class='typedoc__union__type'>
        <Type @info={{type}} />
      </div>
    {{/each}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const literalAsString = (x1: LiteralType['value'])=>{
    if (typeof x1 === 'string') {
        return `"${x1}"`;
    }
    if (typeof x1 === 'number' || typeof x1 === 'boolean' || x1 === null) {
        return `${x1}`;
    }
    return x1.toString();
};
const Literal: TOC<{
    Args: {
        info: LiteralType;
    };
}> = template(`
  <div class='typedoc__literal'>
    {{literalAsString @info.value}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export const Type: TOC<{
    Args: {
        info: SomeType;
    };
}> = template(`
  {{#if (isReference @info)}}
    {{! @glint-expect-error }}
    <Reference @info={{@info}} />
  {{else if (isReflection @info)}}
    {{! @glint-expect-error }}
    <Reflection @info={{@info}} />
  {{else if (isIntrinsic @info)}}
    {{! @glint-expect-error }}
    <Intrinsic @info={{@info}} />
  {{else if (isTuple @info)}}
    {{! @glint-expect-error }}
    <Tuple @info={{@info}} />
  {{else if (isNamedTuple @info)}}
    <NamedTuple @info={{@info}} />
  {{else if (isVoidIntrinsic @info)}}
    {{! @glint-expect-error }}
    <VoidIntrinsic @info={{@info}} />
  {{else if (isArray @info)}}
    <Array @info={{@info}} />
  {{else if (isFn @info)}}
    {{! @glint-expect-error }}
    <Function @info={{@info}} />
  {{else if (isUnion @info)}}
    <Union @info={{@info}} />
  {{else if (isLiteral @info)}}
    <Literal @info={{@info}} />
  {{else if (isUnknownType @info)}}
    <Unknown @info={{@info}} />
  {{else}}
    {{! template-lint-disable no-log }}
    {{log 'Unknown Type' @info}}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
//# sourceMappingURL=data:application/json;base64,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